import { template } from "@ember/template-compiler";
import { hash } from '@ember/helper';
import { Compiled } from '../services/kolay/compiler/reactive.ts';
import { Load } from './utils.gts';
import type { TOC } from '@ember/component/template-only';
import type { ArrayType, DeclarationReference, DeclarationReflection, LiteralType, NamedTupleMember, ReferenceType, SignatureReflection, SomeType, TupleType, UnionType, UnknownType } from 'typedoc';
/**
 * Assumptions:
 * - we are documenting public API
 *   - component properties and methods are not public API
 *     - including the constructor, inherited methods, etc
 *   - only the signature describes what the public API is.
 */ export const APIDocs: TOC<{
    Args: {
        /**
     * Which module to import the type from
     */ module: string;
        /**
     * The name of the export to render the type / JSDoc of
     */ name: string;
        /**
     * The name of the package to lookup the module and export name.
     */ package: string;
    };
}> = template(`
  <Load @module={{@module}} @name='{{@name}}' @package={{@package}} as |info|>
    <Declaration @info={{info}} />
  </Load>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
/**
 * Used for referencing the comment on a const or class.
 *
 * For example:
 * ```
 * /*
 *  * Comment block here is what is targeted
 *  *\/
 * export const CommentQuery ...
 * ```
 *
 * Usage:
 * ```hbs
 * <CommentQuery @name="CommentQuery" ... />
 * ```
 */ export const CommentQuery: TOC<{
    Args: {
        /**
     * Which module to import the type from
     */ module: string;
        /**
     * The name of the export to render the type / JSDoc of
     */ name: string;
        /**
     * The name of the package to lookup the module and export name.
     */ package: string;
    };
}> = template(`
  <Load @package={{@package}} @module={{@module}} @name={{@name}} as |info|>
    <Comment @info={{info}} />
  </Load>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const join = (lines1: string[])=>lines1.join('\n');
const text = (lines1: {
    text: string;
}[])=>lines1.map((line1)=>line1.text);
export function isGlimmerComponent(info1: DeclarationReference) {
    let extended1 = (info1 as any)?.extendedTypes?.[0];
    if (!extended1) return false;
    return (extended1.name === 'default' && extended1.package === '@glimmer/component');
}
export const Comment: TOC<{
    Args: {
        info: {
            comment?: {
                summary?: {
                    text: string;
                }[];
            };
        };
    };
}> = template(`
  {{#if @info.comment.summary}}
    {{#let (Compiled (join (text @info.comment.summary))) as |compiled|}}
      {{#if compiled.isReady}}
        <div class='typedoc-rendered-comment'>
          <compiled.component />
        </div>
      {{/if}}
    {{/let}}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const isIgnored = (name1: string)=>[
        '__type',
        'TOC',
        'TemplateOnlyComponent'
    ].includes(name1);
const isConst = (x1: {
    flags: {
        isConst: boolean;
    };
})=>x1.flags.isConst;
const not = (x1: unknown)=>!x1;
const Declaration: TOC<{
    Args: {
        info: DeclarationReflection | undefined;
    };
}> = template(`
  {{#if @info}}
    <div class='typedoc__declaration'>
      {{#if (not (isIgnored @info.name))}}
        <span class='typedoc__declaration-name'>{{@info.name}}</span>
      {{/if}}

      {{#if (isConst @info)}}
        <Comment @info={{@info}} />
      {{/if}}

      {{#if @info.type}}
        <Type @info={{@info.type}} />
      {{/if}}

      {{#if @info.children}}
        <ul class='typedoc__declaration-children'>
          {{#each @info.children as |child|}}
            <li><Declaration @info={{child}} /></li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if @info.signatures}}
        <ul class='typedoc__declaration-signatures'>
          {{#each @info.signatures as |child|}}
            {{! @glint-expect-error }}
            <li><Type @info={{child}} /></li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if (not (isConst @info))}}
        {{#if @info.comment.summary}}
          <Comment @info={{@info}} />
        {{/if}}
      {{/if}}
    </div>
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Reflection: TOC<{
    info: {
        declaration: DeclarationReflection;
    };
}> = template(`<Declaration @info={{@info.declaration}} />`, {
    eval () {
        return eval(arguments[0]);
    }
});
const isReference = (x1: {
    type: string;
})=>x1?.type === 'reference';
const isReflection = (x1: {
    type: string;
})=>x1?.type === 'reflection';
export const isIntrinsic = (x1: {
    type: string;
})=>x1?.type === 'intrinsic';
const isTuple = (x1: {
    type: string;
})=>x1?.type === 'tuple';
export const isNamedTuple = (x1: SomeType | undefined): x is NamedTupleMember =>x1?.type === 'namedTupleMember';
const isVoidIntrinsic = (x1: unknown | undefined)=>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    if (typeof x1.type === 'object' && x1.type !== null) {
        if ('type' in x1.type && 'name' in x1.type) {
            return x1.type.type === 'intrinsic' && x1.type.name === 'void';
        }
    }
    return false;
};
const isArray = (x1: SomeType | undefined): x is ArrayType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'array';
};
const isFn = (x1: SomeType | undefined)=>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('name' in x1)) return false;
    if (!('variant' in x1)) return false;
    return x1.variant === 'signature';
};
const isUnknownType = (x1: SomeType | undefined): x is UnknownType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'unknown';
};
const isUnion = (x1: SomeType | undefined): x is UnionType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'union';
};
const isLiteral = (x1: SomeType | undefined): x is UnionType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'literal';
};
// function typeArg(info: DeclarationReference) {
//   let extended = info?.extendedTypes?.[0]
//   if (!extended) return false;
//   return extended.typeArguments[0]
// }
//
const isInvokable = (info1: ReferenceType)=>info1.name === 'Invokable';
const Reference: TOC<{
    info: ReferenceType;
}> = template(`
  {{#if (isInvokable @info)}}
    <div class='typedoc__unknown__yield'>
      <Intrinsic @info={{hash name='Component'}} />
    </div>
  {{else}}
    <div class='typedoc__reference'>
      {{#if (not (isIgnored @info.name))}}
        <div class='typedoc__reference__name'>{{@info.name}}</div>
      {{/if}}
      {{#if @info.typeArguments.length}}
        <div class='typedoc__reference__typeArguments'>
          &lt;
          {{#each @info.typeArguments as |typeArg|}}
            <div class='typedoc__reference__typeArgument'>
              <Type @info={{typeArg}} />
            </div>
          {{/each}}
          &gt;
        </div>
      {{/if}}
    </div>
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Intrinsic: TOC<{
    info: {
        name: string;
    };
}> = template(`
  <span class='typedoc__intrinsic'>{{@info.name}}</span>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const VoidIntrinsic: TOC<{
    info: {
        name: string;
    };
}> = template(`
  <div class='typedoc__void_intrinsic'>
    {{! @glint-expect-error }}
    <Function @info={{@info}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Tuple: TOC<{
    Args: {
        info: TupleType;
    };
}> = template(`
  {{#each @info.elements as |element|}}
    <Type @info={{element}} />
  {{/each}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export const NamedTuple: TOC<{
    Args: {
        info: NamedTupleMember;
    };
}> = template(`
  <div class='typedoc__named-tuple'>
    <div class='typedoc__name'>{{@info.name}}</div>
    <Type @info={{@info.element}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Array: TOC<{
    Args: {
        info: ArrayType;
    };
}> = template(`
  <div class='typedoc__array'>
    <div class='typedoc__array__indicator'>Array of</div>
    <Type @info={{@info.elementType}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Function: TOC<{
    Args: {
        info: SignatureReflection;
    };
}> = template(`
  <div class='typedoc__function'>
    <div class='typedoc__function_comment'>
      <Comment @info={{@info}} />
    </div>
    <div class='typedoc__function__type'>
      <div class='typedoc__function__open'>(</div>
      <div class='typedoc__function__parameters'>
        {{#each @info.parameters as |param|}}
          <div class='typedoc__function__parameter__container'>
            <div class='typedoc__function__parameter'>
              <div
                class='typedoc__function__parameter__name'
              >{{param.name}}</div>
              <div class='typedoc__function__parameter__type'>
                {{! @glint-expect-error }}
                <Type @info={{param.type}} />
              </div>
            </div>
            <div class='typedoc__function__parameter__comment'>
              <Comment @info={{param}} />
            </div>
          </div>
        {{/each}}
      </div>
      <div class='typedoc__function__close'>) =></div>
      <div class='typedoc__function__return_type'>
        {{! @glint-expect-error }}
        <Type @info={{@info.type}} />
      </div>
    </div>
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Unknown: TOC<{
    Args: {
        info: any;
    };
}> = template(`
  <div class='typedoc__unknown'>
    {{@info.name}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Union: TOC<{
    Args: {
        info: UnionType;
    };
}> = template(`
  <div class='typedoc__union'>
    {{#each @info.types as |type|}}
      <div class='typedoc__union__type'>
        <Type @info={{type}} />
      </div>
    {{/each}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const literalAsString = (x1: LiteralType['value'])=>{
    if (typeof x1 === 'string') {
        return `"${x1}"`;
    }
    if (typeof x1 === 'number' || typeof x1 === 'boolean' || x1 === null) {
        return `${x1}`;
    }
    return x1.toString();
};
const Literal: TOC<{
    Args: {
        info: LiteralType;
    };
}> = template(`
  <div class='typedoc__literal'>
    {{literalAsString @info.value}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export const Type: TOC<{
    Args: {
        info: SomeType;
    };
}> = template(`
  {{#if (isReference @info)}}
    {{! @glint-expect-error }}
    <Reference @info={{@info}} />
  {{else if (isReflection @info)}}
    {{! @glint-expect-error }}
    <Reflection @info={{@info}} />
  {{else if (isIntrinsic @info)}}
    {{! @glint-expect-error }}
    <Intrinsic @info={{@info}} />
  {{else if (isTuple @info)}}
    {{! @glint-expect-error }}
    <Tuple @info={{@info}} />
  {{else if (isNamedTuple @info)}}
    <NamedTuple @info={{@info}} />
  {{else if (isVoidIntrinsic @info)}}
    {{! @glint-expect-error }}
    <VoidIntrinsic @info={{@info}} />
  {{else if (isArray @info)}}
    <Array @info={{@info}} />
  {{else if (isFn @info)}}
    {{! @glint-expect-error }}
    <Function @info={{@info}} />
  {{else if (isUnion @info)}}
    <Union @info={{@info}} />
  {{else if (isLiteral @info)}}
    <Literal @info={{@info}} />
  {{else if (isUnknownType @info)}}
    <Unknown @info={{@info}} />
  {{else}}
    {{! template-lint-disable no-log }}
    {{log 'Unknown Type' @info}}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
//# sourceMappingURL=data:application/json;base64,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